import { supabase } from '@config/supabase';

export class FolderService {
  /**
   * Convert database folder to TypeScript Folder interface
   */
  convertToFolder(dbFolder: any): any {
    return {
      id: dbFolder.id,
      name: dbFolder.name,
      description: dbFolder.description,
      parentId: dbFolder.parent_id || undefined, // Convert parent_id to parentId
      files: [],
      subfolders: [],
      color: dbFolder.color,
      icon: dbFolder.icon,
      createdAt: dbFolder.created_at,
      updatedAt: dbFolder.updated_at,
    };
  }

  /**
   * Get all folders for current user
   */
  async getFolders(): Promise<any[]> {
    const user = (await supabase.auth.getUser()).data.user;
    if (!user) throw new Error('User not authenticated');

    const { data, error } = await supabase
      .from('folders')
      .select('*')
      .eq('user_id', user.id)
      .order('created_at', { ascending: false });

    if (error) throw error;
    
    return (data || []).map(folder => this.convertToFolder(folder));
  }

  /**
   * Create a new folder
   */
  async createFolder(name: string, parentId?: string): Promise<any> {
    const user = (await supabase.auth.getUser()).data.user;
    if (!user) throw new Error('User not authenticated');

    const insertData: any = {
      user_id: user.id,
      name,
    };

    if (parentId !== undefined && parentId !== null && parentId !== '') {
      insertData.parent_id = parentId;
    } else {
      insertData.parent_id = null;
    }

    console.log('Creating folder with data:', insertData);

    const { data, error } = await supabase
      .from('folders')
      .insert(insertData)
      .select()
      .single();

    if (error) {
      console.error('Error creating folder:', error);
      throw error;
    }

    console.log('Folder created successfully:', data);
    return this.convertToFolder(data);
  }

  /**
   * Update folder
   */
  async updateFolder(id: string, updates: any): Promise<any> {
    const finalUpdates: any = {};
    
    if ('parentId' in updates) {
      if (updates.parentId === null) {
        finalUpdates.parent_id = null;
        console.log('Setting parent_id to null (move to root)');
      } else if (updates.parentId !== undefined && updates.parentId !== '') {
        finalUpdates.parent_id = updates.parentId;
        console.log('Setting parent_id to:', updates.parentId);
      }
    }
    
    if (updates.name !== undefined) {
      finalUpdates.name = updates.name;
    }

    console.log('Updating folder:', id, 'with updates:', finalUpdates);

    if (Object.keys(finalUpdates).length === 0) {
      console.warn('No updates to apply for folder:', id);
      const { data: currentFolder } = await supabase
        .from('folders')
        .select('*')
        .eq('id', id)
        .single();
      return currentFolder ? this.convertToFolder(currentFolder) : null;
    }

    const { data, error } = await supabase
      .from('folders')
      .update(finalUpdates)
      .eq('id', id)
      .select()
      .single();

    if (error) {
      console.error('Error updating folder:', error);
      throw error;
    }

    console.log('Folder updated successfully:', data);
    return this.convertToFolder(data);
  }

  /**
   * Delete folder and all its contents (files and subfolders) recursively
   */
  async deleteFolder(id: string, deleteFileFn: (fileId: string, permanent: boolean) => Promise<void>): Promise<void> {
    console.log('🗑️ Deleting folder:', id);
    
    const { data: folderFiles, error: filesError } = await supabase
      .from('files')
      .select('id, s3_key, name, user_id')
      .eq('folder_id', id);

    if (filesError) {
      console.error('❌ Error fetching folder files:', filesError);
      throw filesError;
    }

    if (folderFiles && folderFiles.length > 0) {
      console.log(`📁 Found ${folderFiles.length} file(s) in folder, deleting...`);
      for (const file of folderFiles) {
        try {
          await deleteFileFn(file.id, true);
          console.log(`✅ Deleted file: ${file.name}`);
        } catch (error) {
          console.error(`❌ Failed to delete file ${file.name}:`, error);
        }
      }
    }

    const { data: subfolders, error: foldersError } = await supabase
      .from('folders')
      .select('id')
      .eq('parent_id', id);

    if (foldersError) {
      console.error('❌ Error fetching subfolders:', foldersError);
      throw foldersError;
    }

    if (subfolders && subfolders.length > 0) {
      console.log(`📁 Found ${subfolders.length} subfolder(s), deleting recursively...`);
      for (const subfolder of subfolders) {
        try {
          await this.deleteFolder(subfolder.id, deleteFileFn);
          console.log(`✅ Deleted subfolder: ${subfolder.id}`);
        } catch (error) {
          console.error(`❌ Failed to delete subfolder ${subfolder.id}:`, error);
        }
      }
    }

    const { error } = await supabase
      .from('folders')
      .delete()
      .eq('id', id);

    if (error) {
      console.error('❌ Error deleting folder:', error);
      throw error;
    }

    console.log('✅ Folder deleted successfully:', id);
  }
}

export const folderService = new FolderService();
export default folderService;
